// Watching a recorded match.
//
// A replay is `{ seed, actions }` and almost nothing else, because the engine
// is deterministic: the decks, the shuffle and every draw come back from the
// seed, and the position after move N is whatever applying the first N actions
// reaches. So a viewer needs no new engine and no stored board -- it is the
// same trick `Session.rewind` already plays, which is a replay of the match
// minus its tail.
//
// It satisfies `Match`, so the board and the HUD draw a replay with no idea
// they are looking at one. What it does not do is accept input: `humanActions`
// is empty, exactly as it is for a spectator, so every gesture is simply never
// legal and nothing has to be disabled by hand.

import { MatchLog, type Replay } from './log.ts';
import { decksFor } from './session.ts';
import { buildContext, type BuildContext } from '../rules/deck.ts';
import { advance, applyAction, createGame, type DeckList } from '../rules/engine.ts';
import type { LoadedRules } from '../rules/load.ts';
import type { Action, GameEvent, GameState, PlayerId } from '../rules/types.ts';
import type { Arrangement } from './layout.ts';

/** Which side the engine was waiting on when this action was taken. */
function actorOf(state: GameState): PlayerId {
  const w = state.waiting;
  return 'player' in w ? w.player : state.turnPlayer;
}

export class ReplayMatch {
  readonly rules: LoadedRules;
  readonly ctx: BuildContext;
  /** The seat the recording was made from, so "you" means the same person. */
  readonly human: PlayerId;
  readonly decks: [DeckList, DeckList];

  state: GameState;
  log: MatchLog;

  /** Meaningless here, but the settings panel writes it without asking. */
  aiDelayMs = 0;
  onChange: (() => void) | null = null;
  onEvents: ((events: GameEvent[]) => void) | null = null;

  /** A recording is watched, never played: nothing may be taken back. */
  readonly canRewind = false;
  readonly watching = true;
  readonly arrangement: Arrangement = {};

  private readonly source: Replay;
  private readonly startSeed: number;
  /** How many actions have been applied. 0 is the opening board. */
  private cursor = 0;
  private eventCursor = 0;

  constructor(rules: LoadedRules, replay: Replay) {
    this.rules = rules;
    this.ctx = buildContext(rules.index, rules.available);
    this.source = replay;
    this.human = replay.human;

    // A replay that names its decks was played with chosen ones and cannot be
    // rebuilt from the seed; one that does not is reproduced by the seed
    // alone, which is the property the whole format rests on.
    const dealt = decksFor(rules, this.ctx, replay.seed, { human: replay.human });
    this.decks = replay.decks ?? dealt.decks;
    // A recording that names its own start seed was made somewhere the seed
    // string does not settle the shuffle -- a server match, which builds its
    // decks its own way, so where its deck builder stopped is not where this
    // one would.
    this.startSeed = replay.startSeed ?? dealt.startSeed;

    this.state = createGame(rules, this.decks, this.startSeed);
    this.log = new MatchLog(rules, this.human);
    advance(this.state, rules);
    this.eventCursor = this.state.events.length;
  }

  /** How many moves the recording holds. */
  get length(): number {
    return this.source.actions.length;
  }

  /** How many have been played back. */
  get position(): number {
    return this.cursor;
  }

  get atEnd(): boolean {
    return this.cursor >= this.length;
  }

  get isOver(): boolean {
    return this.state.waiting.kind === 'gameOver';
  }

  /** Never: a viewer is never the one being waited on. */
  get waitingOnHuman(): boolean {
    return false;
  }

  /**
   * Nothing. A recording has already happened, and the board asks this to
   * decide what may be clicked -- so an empty list disables every gesture at
   * once rather than each of them separately.
   */
  humanActions(): Action[] {
    return [];
  }

  submit(): boolean {
    return false;
  }

  rewind(): boolean {
    return false;
  }

  arrange(): void {
    /* A recorded match's board is not the watcher's to rearrange. */
  }

  replay(): Replay {
    return this.source;
  }

  /** Events the view has not animated yet, marked consumed. */
  drainEvents(): GameEvent[] {
    const fresh = this.state.events.slice(this.eventCursor);
    this.eventCursor = this.state.events.length;
    return fresh;
  }

  /** One move on. Returns whether there was one. */
  step(): boolean {
    if (this.atEnd) return false;
    this.applyNext();
    this.notify();
    return true;
  }

  /**
   * Jumps to the position after `n` moves.
   *
   * Forwards is cheap -- apply the moves in between. Backwards there is
   * nothing to undo, so the game is rebuilt from the seed and fast-forwarded,
   * which is the same thing `rewind` does and costs a few milliseconds even at
   * the end of a long match.
   */
  seek(n: number): void {
    const target = Math.max(0, Math.min(this.length, Math.trunc(n)));
    if (target === this.cursor) return;

    if (target < this.cursor) {
      this.state = createGame(this.rules, this.decks, this.startSeed);
      this.log = new MatchLog(this.rules, this.human);
      advance(this.state, this.rules);
      this.cursor = 0;
    }
    while (this.cursor < target && this.applyNext()) { /* fast-forward */ }

    // Everything up to here has been replayed rather than played, so the view
    // must not animate the whole match as though it had just happened.
    this.eventCursor = this.state.events.length;
    this.notify();
  }

  /**
   * Applies the next recorded action, and records who took it.
   *
   * The actor is not stored in the file and does not need to be: the engine
   * was waiting on somebody when the move was made, and replaying reaches that
   * same position, so the state says whose move it is. Storing it would be a
   * second copy of a fact the engine already holds, free to disagree.
   */
  private applyNext(): boolean {
    const action = this.source.actions[this.cursor];
    if (!action) return false;
    const actor = actorOf(this.state);
    const before = this.state.events.length;
    if (!applyAction(this.state, this.rules, action)) {
      // A recording that no longer applies is a recording from a different
      // build of the rules. Stopping is honest; carrying on would show a match
      // that never happened.
      this.cursor = this.length;
      return false;
    }
    advance(this.state, this.rules);
    this.log.record(this.state, actor, action, this.state.events.slice(before));
    this.cursor++;
    return true;
  }

  private notify(): void {
    this.onChange?.();
    const fresh = this.drainEvents();
    if (fresh.length > 0) this.onEvents?.(fresh);
  }
}
